import { Link } from "react-router";
import { Send } from "lucide-react";

import { Button, type ButtonSize } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { useCompleteness } from "@/features/profile/hooks";
import { useSendBack, useSentBack } from "@/features/profile/useSendBack";

// The button that sends a sent-back profile to HR again, and the banner that
// carries it on every page of the form. useSendBack says why.

/** What is still to fill in before it can go, and where the first of it is. */
function useStillMissing() {
  const { data: completeness } = useCompleteness();
  const missing = (completeness?.steps ?? []).flatMap((step) =>
    step.missing.map((field) => ({ ...field, stepKey: step.key })),
  );
  return { ready: completeness?.complete ?? false, loaded: Boolean(completeness), missing };
}

/** "Send back to HR", enabled once nothing required is missing. */
export function SendBackButton({
  size = "md",
  className,
  variant = "primary",
  explain = true,
}: {
  size?: ButtonSize;
  className?: string;
  variant?: "primary" | "secondary";
  /** Say what is missing under a greyed-out button; the banner says it itself. */
  explain?: boolean;
}) {
  const { send, sending, error } = useSendBack();
  const { ready, loaded, missing } = useStillMissing();
  const first = missing[0];
  return (
    <div className={className}>
      <Button
        type="button"
        variant={variant}
        size={size}
        icon={<Send className="size-4" />}
        onClick={() => void send()}
        loading={sending}
        disabled={!ready}
      >
        Send back to HR
      </Button>
      {error ? (
        <p className="mt-1.5 text-xs font-medium text-alert-700" role="alert">
          {error}
        </p>
      ) : explain && loaded && !ready && first ? (
        // A greyed-out button with no reason is how a profile sits unsent.
        <p className="mt-1.5 text-xs text-fg-muted">
          Before it can go:{" "}
          <Link
            to={`/onboarding/${first.stepKey}`}
            className="font-semibold text-accent underline underline-offset-2 hover:no-underline"
          >
            {first.label}
          </Link>
          {missing.length > 1 ? ` and ${missing.length - 1} more` : ""} still to fill in.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The slim banner at the head of every page of the form while HR has asked
 * for changes: what to do when they are done, and the button to do it. When
 * something required is missing it says what, and links to it.
 */
export function SendBackBanner({ className }: { className?: string }) {
  const sentBack = useSentBack();
  const { ready, loaded, missing } = useStillMissing();
  if (!sentBack) return null;

  const first = missing[0];
  return (
    <div
      role="status"
      className={cn(
        "flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-md bg-ochre-50 px-4 py-3 text-ochre-700",
        className,
      )}
    >
      <div className="min-w-0 text-base">
        <p className="font-semibold">HR asked for changes. When you have fixed them, send it back.</p>
        {loaded && !ready && first ? (
          <p className="mt-0.5 text-xs">
            Before it can go:{" "}
            <Link
              to={`/onboarding/${first.stepKey}`}
              className="font-semibold underline underline-offset-2 hover:no-underline"
            >
              {first.label}
            </Link>
            {missing.length > 1 ? ` and ${missing.length - 1} more` : ""} still to fill in.
          </p>
        ) : (
          <p className="mt-0.5 text-xs">Saving keeps your changes; HR sees them once you send it.</p>
        )}
      </div>
      <SendBackButton size="sm" explain={false} />
    </div>
  );
}
